import React from "react";
import { Box, Flex, VStack, Text, Grid, FormControl, FormLabel, Image as ChakraImage, InputGroup, InputLeftElement } from "@chakra-ui/react";
import Image from "next/image";
import { P } from "../components/Type";
import Button from "../components/Button";
import { Trans, t, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useRouter } from "next/router";
import { Formik, Form, Field } from "formik";
import { object, string } from "yup";
import SecureIcon from "../assets/secure.png";
import SearchIcon from "../assets/search.png";
import { Input } from "../components/Input";
import { useGameVoucherGroups } from "../hooks/game-vouchers/useGameVoucherGroups";

const GameCard = ({ game, onClick }) => {
  return (
    <Box
      w={{ base: "full", md: "full" }}
      h="100%"
      p={{ base: "12px", md: "16px" }}
      bg="#F2F7F5"
      overflow="hidden"
      borderRadius="6px"
      border="1px solid #BEC1C1"
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      gap={{ base: "8px", md: "16px" }}
      onClick={onClick}
      cursor="pointer"
      _hover={{ transform: "translateY(-2px)", transition: "0.2s" }}
    >
      <Box w="100%" display={{ base: "block", md: "flex" }} gap="16px">
        <Box
          position="relative"
          w={{ base: "100%", md: "100%" }}
          h={{ base: "auto", md: "296px" }}
          overflow="hidden"
          borderRadius="6px"
          aspectRatio={{ base: "16/9", md: "auto" }}
        >
          <ChakraImage
            src={game.cover}
            alt={game.name}
            w="100%"
            h="100%"
            objectFit="cover"
            borderRadius="6px"
          />
        </Box>
      </Box>

      <VStack
        flex={1}
        bg="#F2F7F5"
        spacing="8px"
        align="flex-start"
        justify="space-between"
        w="100%"
        display={{ base: "flex", md: "none" }}
      >
        <Text
          fontSize={{ base: "14px", md: "16px" }}
          lineHeight={{ base: "20px", md: "24px" }}
          fontFamily="IBM Plex Mono"
          color="#000"
          w="100%"
        >
          {game.name}
        </Text>
        <Box
          fontSize="10px"
          lineHeight="16px"
          fontFamily="IBM Plex Mono"
          color="#000"
          bg="#EAEFED"
          p="6px"
          borderRadius="4px"
          display="flex"
          alignItems="center"
          gap="6px"
          mt="auto"
        >
          <Box display="flex" alignItems="center" mr="6px" flexShrink={0}>
            <Image src={SecureIcon} alt="" width={10} height={12} />
          </Box>
          By {game.publisher}
        </Box>
      </VStack>
      
      <VStack
        bg="#F2F7F5"
        spacing="8px"
        align="flex-start"
        w="100%"
        display={{ base: "none", md: "flex" }}
        mt="16px"
        justify="space-between"
        flex="1"
      >
        <Text
          fontSize="16px"
          lineHeight="24px"
          fontFamily="IBM Plex Mono"
          color="#000"
          w="100%"
        >
          {game.name}
        </Text>
        <Box
          fontSize="14px"
          lineHeight="20px"
          fontFamily="IBM Plex Mono"
          color="#000"
          bg="#EAEFED"
          p="8px"
          borderRadius="4px"
          display="flex"
          alignItems="center"
          gap="8px"
          mt="auto"
        >
          <Box mr="8px" display="flex" alignItems="center" flexShrink={0}>
            <Image src={SecureIcon} alt="" width={14} height={16} />
          </Box>
          By {game.publisher}
        </Box>
      </VStack>
    </Box>
  );
};

export const VoucherForm = ({
  streamerId,
  isGopayEnabled,
  isOvoEnabled,
  isDanaEnabled,
  isLinkAjaEnabled,
  isQRISEnabled,
  isBCAVAEnabled,
  isMandiriVAEnabled,
  isBRIVAEnabled,
  isBNIVAEnabled,
  isPermataVAEnabled,
  isBJBVAEnabled,
  isBSIVAEnabled,
  isCIMBVAEnabled,
}) => {
  const [activeTab, setActiveTab] = React.useState("VOUCHER");
  const [keyword, setKeyword] = React.useState("");
  const { _ } = useLingui();
  const router = useRouter();
  const { username } = router.query;

  const paymentFlags = React.useMemo(
    () => ({
      isGopayEnabled: String(!!isGopayEnabled),
      isOvoEnabled: String(!!isOvoEnabled),
      isDanaEnabled: String(!!isDanaEnabled),
      isLinkAjaEnabled: String(!!isLinkAjaEnabled),
      isQRISEnabled: String(!!isQRISEnabled),
      isBCAVAEnabled: String(!!isBCAVAEnabled),
      isMandiriVAEnabled: String(!!isMandiriVAEnabled),
      isBRIVAEnabled: String(!!isBRIVAEnabled),
      isBNIVAEnabled: String(!!isBNIVAEnabled),
      isPermataVAEnabled: String(!!isPermataVAEnabled),
      isBJBVAEnabled: String(!!isBJBVAEnabled),
      isBSIVAEnabled: String(!!isBSIVAEnabled),
      isCIMBVAEnabled: String(!!isCIMBVAEnabled),
    }),
    [
      isGopayEnabled,
      isOvoEnabled,
      isDanaEnabled,
      isLinkAjaEnabled,
      isQRISEnabled,
      isBCAVAEnabled,
      isMandiriVAEnabled,
      isBRIVAEnabled,
      isBNIVAEnabled,
      isPermataVAEnabled,
      isBJBVAEnabled,
      isBSIVAEnabled,
      isCIMBVAEnabled,
    ]
  );

  const { data, canFetchMore, isFetchingMore, fetchMore } = useGameVoucherGroups({ 
    keyword: keyword.length >= 3 ? keyword : "",
    username: username || "",
    streamer_id: streamerId || "",
  });
  const observerRef = React.useRef(null);
  const UNRESTRICTED_USERNAMES = (process.env.NEXT_PUBLIC_GAME_WHITELIST_USERNAMES || '').split(',').filter(Boolean);
  
  const games = React.useMemo(() => {
    const allGames = data?.flatMap(page => page.product_groups) || [];
    if (process.env.NEXT_PUBLIC_GAME_WHITELIST_ENABLED !== 'true') return allGames;

    if (UNRESTRICTED_USERNAMES.includes(username)) return allGames;
    return allGames.filter(game => {
      const product_code = game.code?.toLowerCase();
      return product_code === "pre-vc-garena";
    });
  }, [data, username]);


  React.useEffect(() => {
    if (activeTab !== "VOUCHER") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && canFetchMore && !isFetchingMore) {
          fetchMore();
        }
      },
      { threshold: 0.1 }
    );
    if (observerRef.current) observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, [activeTab, canFetchMore, isFetchingMore, fetchMore]);

  return (
    <Box>
      <VStack
        spacing="0"
        align="flex-start"
        w="100%"
        mt={4}
      >
        <Flex direction="row" justifyContent="left" w="100%" overflowX="auto" mb={{ base: "12px", md: "16px" }}>
          <Button
            label={_(msg`Produk`)}
            pressed={activeTab === "VOUCHER"}
            backgroundColor={
              activeTab === "VOUCHER"
                ? "saweria-yellow.normal"
                : "saweria-white.normal"
            }
            px={{ base: 2, md: 6 }}
            flexShrink={0}
            mr={{ base: 2, md: 4 }}
            mb={{ base: 2, md: 4 }}
            boxShadow="none"
            fontSize={{ base: "sm", md: "lg" }}
            onClick={() => setActiveTab("VOUCHER")}
          />
          <Button
            label={_(msg`Track Order`)}
            pressed={activeTab === "TRACK"}
            backgroundColor={
              activeTab === "TRACK"
                ? "saweria-yellow.normal"
                : "saweria-white.normal"
            }
            px={{ base: 2, md: 6 }}
            flexShrink={0}
            mb={{ base: 2, md: 4 }}
            boxShadow="none"
            fontSize={{ base: "sm", md: "lg" }}
            onClick={() => setActiveTab("TRACK")}
          />
        </Flex>

        {activeTab === "VOUCHER" ? (
          <>
          <Box w="100%" mb="16px">
            <Text
              fontSize={{ base: "14px", md: "16px" }}
              fontFamily="IBM Plex Mono"
              lineHeight={{ base: "20px", md: "24px" }}
              color="#1a202c"
              mb={{ base: "12px", md: "16px" }}
            >
              <Trans>Top up game favorit sambil dukung kreator kesayangan</Trans>
            </Text>
            
            <InputGroup mb={{ base: "16px", md: "20px" }}>
              <InputLeftElement pointerEvents="none" h="100%">
                <Image src={SearchIcon} alt="" width={16} height={16} />
              </InputLeftElement>
              <Input
                placeholder={_(msg`Cari game`)}
                pl="40px"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </InputGroup>
             </Box>
            
              <Grid
                templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(2, 1fr)" }}
                gap={{ base: 3, md: 6 }}
                w="100%"
              >
                {games.map((game) => (
                  <GameCard
                    key={game.id}
                    game={game}
                    onClick={() =>
                      router.push(`/${username}/toko-top-up/${game.slug}`)
                    }
                  />
                ))}
              </Grid>
              
              {isFetchingMore && (
                <Text mt={4}><Trans>Memuat...</Trans></Text>
              )}
              
              <Box ref={observerRef} h="20px" w="100%" />
          </>
        ) : (
          <Box w="100%">
            <Text
              w="100%"
              lineHeight="20px"
              fontFamily="IBM Plex Mono"
              color="#1a202c"
              textAlign="left"
              display="inline-block"
              mb="17px"
            >
              <Trans>Lacak pesanan vouchermu dengan memasukkan invoice number / track ID</Trans>
            </Text>
            <Formik
              initialValues={{ trackId: "" }}
              validationSchema={object({
                trackId: string().required(_(msg`Track ID tidak boleh kosong`))
              })}
              onSubmit={(values, { setSubmitting }) => {
                if (values.trackId) {
                  window.open(`/track/${values.trackId}`, '_blank');
                  setSubmitting(false);
                }
              }}
            >
              {({ errors, touched, isSubmitting }) => (
                <Form>
                  <VStack spacing={4} align="flex-start">
                    <FormControl isInvalid={errors.trackId && touched.trackId}>
                      <FormLabel htmlFor="trackId" fontWeight="bold">
                        <Trans>Track ID</Trans>
                      </FormLabel>
                      <Field
                        as={Input}
                        id="trackId"
                        name="trackId"
                        placeholder={_(msg`Masukkan Track ID`)}
                      />
                      {errors.trackId && touched.trackId && (
                        <Text color="red.500" fontSize="sm" mt="4px">
                          {errors.trackId}
                        </Text>
                      )}
                    </FormControl>
                    <Button
                      type="submit"
                      label={_(msg`Lacak Pesanan`)}
                      backgroundColor="saweria-green.normal"
                      pressed={isSubmitting}
                    />
                  </VStack>
                </Form>
              )}
            </Formik>
          </Box>
        )}
      </VStack>
    </Box>
  );
};
